<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Performance Diaria',
  },
});

const activeMetric = ref('conversations');

const metrics = [
  { key: 'conversations', label: 'Conversas', color: '#3b82f6' },
  { key: 'resolved', label: 'Resolvidas', color: '#22c55e' },
  { key: 'resolution_rate', label: 'Taxa %', color: '#8b5cf6' },
  { key: 'avg_response_minutes', label: 'Tempo Resp.', color: '#f59e0b' },
];

const chartData = computed(() => {
  if (!props.data || props.data.length === 0) return [];
  return props.data.map(d => ({
    date: d.date,
    label: formatDate(d.date),
    value: d[activeMetric.value] || 0,
  }));
});

const maxValue = computed(() => {
  return Math.max(...chartData.value.map(d => d.value), 1);
});

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function getBarHeight(value) {
  return (value / maxValue.value) * 100;
}

function getCurrentMetricColor() {
  return metrics.find(m => m.key === activeMetric.value)?.color || '#3b82f6';
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <span class="i-lucide-bar-chart-3 text-lg text-n-blue-10" />
        <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
      </div>
      <div class="flex gap-1">
        <button
          v-for="metric in metrics"
          :key="metric.key"
          class="px-2 py-1 text-xs rounded transition-colors"
          :class="
            activeMetric === metric.key
              ? 'bg-n-blue-9 text-white'
              : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="activeMetric = metric.key"
        >
          {{ metric.label }}
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="chartData.length === 0"
      class="flex items-center justify-center h-48 text-n-slate-11"
    >
      <span class="text-sm">Sem dados para o periodo</span>
    </div>

    <!-- Chart -->
    <div v-else class="flex flex-col gap-2">
      <div class="flex items-end gap-1 h-40">
        <div
          v-for="(item, idx) in chartData"
          :key="idx"
          class="flex-1 flex flex-col items-center justify-end group relative"
        >
          <div
            class="w-full rounded-t transition-all duration-300 hover:opacity-80"
            :style="{
              height: `${getBarHeight(item.value)}%`,
              backgroundColor: getCurrentMetricColor(),
              minHeight: item.value > 0 ? '4px' : '0',
            }"
          />
          <!-- Tooltip -->
          <div class="absolute bottom-full mb-1 hidden group-hover:block z-10">
            <div
              class="bg-n-solid-1 border border-n-weak rounded px-2 py-1 text-xs shadow-lg whitespace-nowrap"
            >
              <div class="font-medium text-n-slate-12">{{ item.label }}</div>
              <div class="text-n-slate-11">{{ item.value }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- X-axis labels -->
      <div class="flex gap-1">
        <div
          v-for="(item, idx) in chartData"
          :key="idx"
          class="flex-1 text-center"
        >
          <span
            v-if="idx % Math.ceil(chartData.length / 7) === 0"
            class="text-xs text-n-slate-10"
          >
            {{ item.label }}
          </span>
        </div>
      </div>

      <!-- Summary -->
      <div
        class="flex items-center justify-between pt-2 border-t border-n-weak text-xs text-n-slate-11"
      >
        <span>{{ chartData.length }} dias</span>
        <span>
          Media:
          {{
            (
              chartData.reduce((acc, d) => acc + d.value, 0) / chartData.length
            ).toFixed(1)
          }}
        </span>
      </div>
    </div>
  </div>
</template>
